/**
 * 导航菜单服务
 * 处理与导航菜单相关的所有API调用
 */

import { get, post, put, del } from './apiClient';
import { getAuthDataFromStorage } from './authService';
import logger from '../utils/logger';

// 导航菜单相关API端点
const API_ENDPOINTS = {
  NAV_ITEMS: '/navs',
  NAV_ITEM: (id) => `/navs/${id}`,
  ADMIN_DEFAULT: '/navs/admin-default'
};

/**
 * 检查用户是否已登录并获取 token
 * @returns {Object|null} 包含 token 的对象或 null
 */
const getAuthToken = () => {
  const authData = getAuthDataFromStorage();
  return authData && authData.token ? { token: authData.token } : null;
};

/**
 * 检查用户是否已登录
 * @returns {boolean} 用户是否已登录
 */
const isUserLoggedIn = () => {
  const authData = getAuthDataFromStorage();
  return !!authData && !!authData.token;
};

/**
 * 获取所有导航菜单项
 * @returns {Promise<{navItems: Array, websitesByNav: Object}>} 导航菜单项数组和对应的网站数据
 */
export const fetchNavItems = async () => {
  logger.log('[Nav] fetchNavItems 开始执行');
  
  // 未登录时返回空数组，由上层逻辑处理本地数据
  if (!isUserLoggedIn()) {
    logger.log('[Nav] 用户未登录，不从API获取导航菜单项');
    return { navItems: [], websitesByNav: {} };
  }

  try {
    // 获取 token 并传递给 API 请求
    const authOptions = getAuthToken();
    if (!authOptions) {
      logger.error('[Nav] 获取认证 token 失败');
      return { navItems: [], websitesByNav: {} };
    }
    
    logger.log('[Nav] 开始向API请求导航菜单项');
    const response = await get(API_ENDPOINTS.NAV_ITEMS, authOptions);
    logger.log('[Nav] API返回导航菜单项成功:', response);
    
    const data = response.data || [];
    logger.log('[Nav] 返回的导航项数量:', data.length);
    
    // 处理导航菜单数据
    const navItems = data.map(nav => ({
      id: nav.id,
      text: nav.text,
      icon: nav.icon,
      index: nav.index
    }));
    
    // 处理每个导航下的网站数据
    const websitesByNav = {};
    data.forEach(nav => {
      websitesByNav[nav.id] = nav.websites || [];
    });
    
    return { navItems, websitesByNav };
  } catch (error) {
    logger.error('[Nav] 获取导航菜单项失败:', error);
    throw error;
  }
};

/**
 * 获取管理员默认导航数据（公开接口，无需登录）
 * @returns {Promise<{navItems: Array, websitesByNav: Object}>} 管理员的导航菜单项数组和对应的网站数据
 */
export const fetchAdminDefaultNavItems = async () => {
  logger.log('[Nav] fetchAdminDefaultNavItems 开始执行');
  
  try {
    logger.log('[Nav] 开始向API请求管理员默认导航菜单项');
    const response = await get(API_ENDPOINTS.ADMIN_DEFAULT);
    logger.log('[Nav] API返回管理员默认导航菜单项成功:', response);
    
    const data = response.data || [];
    logger.log('[Nav] 返回的管理员默认导航项数量:', data.length);
    
    // 处理导航菜单数据
    const navItems = data.map(nav => ({
      id: nav.id,
      text: nav.text,
      icon: nav.icon,
      index: nav.index
    }));
    
    // 处理每个导航下的网站数据
    const websitesByNav = {};
    data.forEach(nav => {
      websitesByNav[nav.id] = nav.websites || [];
    });
    
    return { navItems, websitesByNav };
  } catch (error) {
    logger.error('[Nav] 获取管理员默认导航菜单项失败:', error);
    throw error;
  }
};

/**
 * 添加新的导航菜单项
 * @param {Object} navItem 要添加的导航菜单项数据
 * @returns {Promise<Object>} 添加后的导航菜单项，包含服务端生成的ID
 */
export const addNavItem = async (navItem) => {
  logger.log('[Nav] addNavItem 开始执行:', navItem);
  
  // 未登录时直接返回原始对象，不调用API
  if (!isUserLoggedIn()) {
    logger.log('[Nav] 用户未登录，不向API添加导航菜单项');
    return navItem;
  }

  try {
    // 获取 token 并传递给 API 请求
    const authOptions = getAuthToken();
    if (!authOptions) {
      logger.error('[Nav] 获取认证 token 失败');
      return navItem;
    }
    
    logger.log('[Nav] 开始向API发送添加导航菜单项请求:', navItem);
    const response = await post(API_ENDPOINTS.NAV_ITEMS, navItem, authOptions);
    logger.log('[Nav] API返回添加的导航菜单项:', response.data);
    return response.data; // 返回包含服务端ID的对象
  } catch (error) {
    logger.error('[Nav] 添加导航菜单项失败:', error);
    throw error;
  }
};

/**
 * 更新导航菜单项
 * @param {string} id 要更新的导航菜单项ID
 * @param {Object} updates 要更新的属性
 * @returns {Promise<Object>} 更新后的导航菜单项
 */
export const updateNavItem = async (id, updates) => {
  if (!isUserLoggedIn()) {
    logger.log(`用户未登录，不向API更新导航菜单项 ${id}`);
    return null;
  }

  try {
    const token = await getAuthToken();
    if (!token) {
      logger.error('获取认证 token 失败');
      return null;
    }
    
    const response = await put(API_ENDPOINTS.NAV_ITEM(id), updates, token);
    return response.data;
  } catch (error) {
    logger.error(`更新导航菜单项 ${id} 失败:`, error);
    return null;
  }
};

/**
 * 删除导航菜单项
 * @param {string} id 要删除的导航菜单项ID
 * @returns {Promise<boolean>} 删除成功返回true
 */
export const deleteNavItem = async (id) => {
  if (!isUserLoggedIn()) {
    logger.log(`用户未登录，不向API删除导航菜单项 ${id}`);
    return null;
  }

  try {
    const token = await getAuthToken();
    if (!token) {
      logger.error('获取认证 token 失败');
      return null;
    }
    
    await del(API_ENDPOINTS.NAV_ITEM(id), token);
    return true;
  } catch (error) {
    logger.error(`删除导航菜单项 ${id} 失败:`, error);
    return null;
  }
};

/**
 * 重新排序导航菜单项
 * @param {Array} navItems 重新排序后的完整导航菜单项数组
 * @returns {Promise<Array>} 更新后的导航菜单项数组
 */
export const reorderNavItems = async (navItems) => {
  logger.log('[Nav] reorderNavItems 开始执行, 项目数量:', navItems.length);
  
  // 未登录时直接返回原始数组，不调用API
  if (!isUserLoggedIn()) {
    logger.log('[Nav] 用户未登录，不向API同步导航菜单项顺序');
    return navItems;
  }

  try {
    // 获取 token 并传递给 API 请求
    const authOptions = getAuthToken();
    if (!authOptions) {
      logger.error('[Nav] 获取认证 token 失败');
      return navItems;
    }
    
    logger.log('[Nav] 开始向API发送重排序导航菜单项请求');
    const response = await put(API_ENDPOINTS.NAV_ITEMS, { items: navItems }, authOptions);
    logger.log('[Nav] API返回重排序后的导航菜单项:', response);
    logger.log('[Nav] 返回的导航项数量:', response.data ? response.data.length : 0);
    return response.data || [];
  } catch (error) {
    logger.error('[Nav] 重新排序导航菜单项失败:', error);
    throw error;
  }
};

export default {
  fetchNavItems,
  addNavItem,
  updateNavItem,
  deleteNavItem,
  reorderNavItems
}; 